import React from "react";
import { useTheme } from "../../../../contexts/ThemeContext";

interface ConfirmSubscriptionModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onClose: () => void;
  disabled?: boolean;
  title?: string;
  message?: React.ReactNode;
  iconType?: "success" | "upgrade";
  confirmText?: string;
}

const ConfirmSubscriptionModal: React.FC<ConfirmSubscriptionModalProps> = ({
  isOpen,
  onConfirm,
  onClose,
  disabled = false,
  title = "Subscription confirmation",
  message = "This will activate your subscription and recurring billing. You can cancel anytime.",
  iconType = "success",
  confirmText = "Confirm",
}) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  if (!isOpen) return null;
  console.log(iconType, "iconType");

  return (
    <div
      className="fixed inset-0 z-60 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className={`w-[320px] rounded-lg shadow-xl border relative ${isDark ? "bg-[#1B1B1B] border-[#2A2A2A]" : "bg-white border-gray-200"
          }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button (top-right) */}
        <button
          onClick={onClose}
          aria-label="Close"
          className={`absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-[7.529px] transition-opacity ${isDark
              ? "bg-[#2E2E2E] shadow-[0_-0.941px_0.941px_0_rgba(150,161,172,0.12)_inset] hover:opacity-80"
              : "bg-gray-100 hover:bg-gray-200"
            }`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M12.4235 2.63525L2.63525 12.4235M2.63525 2.63525L12.4235 12.4235"
              stroke={isDark ? "#F3F3F6" : "#4B5563"}
              strokeWidth="1.12942"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        {/* Header icon */}
        <div className="flex justify-center pt-4">
          <div
            className="flex items-center justify-center"
            style={{
              width: 56,
              height: 56,
              borderRadius: 28,
              border: isDark ? "8px solid #2E2E2E" : "8px solid #E5E7EB",
              background: isDark
                ? "linear-gradient(180deg, #1F1F1F 0%, #2E2E2E 100%)"
                : "linear-gradient(180deg, #F3F4F6 0%, #E5E7EB 100%)",
            }}
          >
            {iconType === "upgrade" ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M2.5 12C2.5 16.4783 2.5 18.7175 3.89124 20.1088C5.28249 21.5 7.52166 21.5 12 21.5C16.4783 21.5 18.7175 21.5 20.1088 20.1088C21.5 18.7175 21.5 16.4783 21.5 12C21.5 7.5217 21.5 5.2825 20.1088 3.8912C18.7175 2.5 16.4783 2.5 12 2.5C7.52166 2.5 5.28249 2.5 3.89124 3.8912C2.5 5.2825 2.5 7.5217 2.5 12Z" stroke={isDark ? "white" : "#4B5563"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M16 11.5C16 11.5 13.0541 7.5 12 7.5C10.9459 7.5 8 11.5 8 11.5M12 8V16.5" stroke="#C6D92C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M11.3598 2.53354L9.00005 4.50005H5.50005C4.94777 4.50005 4.50005 4.94777 4.50005 5.50005V9.00005L2.53354 11.3598C2.2245 11.7307 2.2245 12.2693 2.53354 12.6402L4.50005 15V18.5C4.50005 19.0523 4.94777 19.5 5.50005 19.5H9.00005L11.3598 21.4665C11.7307 21.7756 12.2693 21.7756 12.6402 21.4665L15 19.5H18.5C19.0523 19.5 19.5 19.0523 19.5 18.5V15L21.4665 12.6402C21.7756 12.2693 21.7756 11.7307 21.4665 11.3598L19.5 9.00005V5.50005C19.5 4.94777 19.0523 4.50005 18.5 4.50005H15L12.6402 2.53354C12.2693 2.2245 11.7307 2.2245 11.3598 2.53354Z"
                  stroke="#C6D92C"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 13L11 15L15.5 9.5"
                  stroke="#C6D92C"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
        </div>

        {/* Title & message */}
        <div className="px-5 pt-3 pb-2 text-center">
          <h3
            className={`text-sm font-semibold ${isDark ? "text-white" : "text-gray-900"
              }`}
          >
            {title}
          </h3>
          <div
            className={`mt-2 text-[11px] leading-relaxed ${isDark ? "text-gray-400" : "text-gray-600"
              }`}
          >
            {message}
          </div>
        </div>

        {/* Footer buttons */}
        <div
          className={`px-5 pt-2 pb-4 flex items-center gap-3 justify-center border-t ${isDark ? "border-[#2A2A2A]" : "border-gray-200"
            }`}
        >
          <button
            onClick={onConfirm}
            disabled={disabled}
            className="min-w-[110px] px-3 h-9 text-[12px] font-semibold text-black rounded-[10px] border flex items-center justify-center whitespace-nowrap"
            style={{
              border: "1px solid var(--menu-active-border, #C6D92C)",
              background:
                "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
              opacity: disabled ? 0.7 : 1,
            }}
          >
            {confirmText}
          </button>
          <button
            onClick={onClose}
            className={`min-w-[110px] h-9 text-[12px] font-medium rounded-[10px] border ${isDark
                ? "border-[#3A3A3A] text-gray-300 hover:bg-white/5"
                : "border-gray-300 text-gray-700 hover:bg-black/5"
              }`}
          >
            Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmSubscriptionModal;
